import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Plus, Loader2, Trash2 } from "lucide-react";
import { listSubscriptions, createSubscription, cancelSubscription } from "../../lib/api/admin-subscriptions";
import { listPlans } from "../../lib/api/admin-plans";
import { api } from "../../lib/api/client";
import type { SubscriptionCreateRequest } from "../../types/usage";

interface UserOption {
  id: string;
  email: string;
  display_name?: string;
}

export function SubscriptionsPage() {
  const queryClient = useQueryClient();
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState<SubscriptionCreateRequest>({ user_id: "", plan_id: "" });

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: listSubscriptions,
  });

  const { data: plans } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: listPlans,
  });

  const { data: users } = useQuery({
    queryKey: ["admin-users-list"],
    queryFn: async (): Promise<UserOption[]> => {
      const response = await api.get("/users", { params: { page: 1, size: 100 } });
      return response.data.users;
    },
  });

  const assignMutation = useMutation({
    mutationFn: (data: SubscriptionCreateRequest) => createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
      setShowAssignForm(false);
      setAssignForm({ user_id: "", plan_id: "" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-brand-muted" />
          <h1 className="text-2xl font-bold text-brand-text">Subscriptions</h1>
        </div>
        <button
          onClick={() => setShowAssignForm(!showAssignForm)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-brand-surface rounded-lg hover:opacity-90"
        >
          <Plus className="w-4 h-4" />
          Assign Plan
        </button>
      </div>

      {showAssignForm && (
        <div className="bg-brand-bg shadow rounded-card p-4 flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-brand-text mb-1">User</label>
            <select
              value={assignForm.user_id}
              onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a user</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}{user.display_name ? ` (${user.display_name})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-brand-text mb-1">Plan</label>
            <select
              value={assignForm.plan_id}
              onChange={(e) => setAssignForm({ ...assignForm, plan_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a plan</option>
              {plans?.filter(p => p.is_active).map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.display_name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => assignMutation.mutate(assignForm)}
            disabled={!assignForm.user_id || !assignForm.plan_id || assignMutation.isPending}
            className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            Assign
          </button>
        </div>
      )}

      <div className="bg-brand-bg shadow rounded-card overflow-hidden">
        <table className="min-w-full divide-y divide-brand-border">
          <thead className="bg-brand-surface">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Usage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Activated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-brand-bg divide-y divide-brand-border">
            {subscriptions?.map((sub) => (
              <tr key={sub.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-text">
                  {sub.user_email || sub.user_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {sub.plan?.display_name || sub.plan_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded text-xs ${
                    sub.status === "active" ? "bg-brand-teal/10 text-brand-teal" :
                    sub.status === "cancelled" ? "bg-brand-surface text-brand-text" :
                    "bg-brand-error text-brand-error"
                  }`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {sub.current_usage !== null && sub.usage_limit !== null
                    ? `${sub.current_usage} / ${sub.usage_limit}`
                    : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {new Date(sub.activated_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sub.status === "active" && (
                    <button
                      onClick={() => cancelMutation.mutate(sub.id)}
                      disabled={cancelMutation.isPending}
                      className="text-brand-error hover:text-brand-error flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
